import { format, parseISO } from "date-fns"
import { MAX_CHARS } from "../constants"

/**
 * Calculates the maximum number of characters based on screen width
 */
export const calculateMaxChars = () => {
  const width = window.innerWidth
  if (width < 768) return MAX_CHARS.MOBILE
  if (width < 1024) return MAX_CHARS.TABLET
  return MAX_CHARS.DESKTOP
}

/**
 * Extracts school name and group number from a group name
 */
export const extractGroupInfo = groupName => {
  if (!groupName) return { schoolName: "", groupNumber: "" }

  // get the last digits of the groupName
  const groupNumber = groupName.match(/\d+$/)?.[0] || ""
  const schoolName = groupName.replace(groupNumber, "")

  return { schoolName, groupNumber }
}

/**
 * Formats event time for display
 */
export const formatEventTime = (time, timeZone) => {
  const date = parseISO(time)
  return `${format(date, "hh:mmaaa")} ${timeZone || "EST"}`
}

/**
 * Formats start and end times for display in modals
 */
export const formatEventTimeRange = (start, end, timeZone) => {
  try {
    if (!end) {
      const startDate = typeof start === "string" ? parseISO(start) : new Date(start)
      return `${format(startDate, "h:mm a")} ${timeZone || "EST"}`
    }

    const startDate = typeof start === "string" ? parseISO(start) : new Date(start)
    const endDate = typeof end === "string" ? parseISO(end) : new Date(end)
    return `${format(startDate, "h:mm a")} - ${format(endDate, "h:mm a")} ${timeZone || "EST"}`
  } catch (error) {
    console.error("Error formatting event time:", error)
    return "Time not available"
  }
}

/**
 * Truncates a full name to fit within maxChars
 */
export const truncateName = (fullName, maxChars) => {
  if (!fullName) return ""

  // If the full name is within limits, return it as is
  if (fullName.length <= maxChars) {
    return fullName
  }

  // Split the name into parts
  const nameParts = fullName.split(" ")
  if (nameParts.length < 2) return fullName.slice(0, maxChars)

  const firstName = nameParts[0]
  const lastName = nameParts[nameParts.length - 1]

  // Calculate available space for first name (prioritize it)
  const spaceForNames = maxChars - 1 // -1 for the space between names
  const firstNameChars = Math.min(firstName.length, Math.ceil(spaceForNames * 0.6)) // Give 60% to first name
  const lastNameChars = Math.min(lastName.length, spaceForNames - firstNameChars)

  const truncatedFirst = firstName.slice(0, firstNameChars)
  const truncatedLast = lastName.slice(0, lastNameChars)

  return `${truncatedFirst}${truncatedLast}`
}

/**
 * Truncates a group name to fit within maxChars
 */
export const truncateGroupName = (groupName, maxChars) => {
  const { schoolName, groupNumber } = extractGroupInfo(groupName)
  if (!schoolName) return groupName

  if (groupNumber) {
    const fullName = `${schoolName}${groupNumber}`
    if (fullName.length <= maxChars) {
      return fullName
    }

    const ellipsis = "..."
    const reservedChars = ellipsis.length + groupNumber.length
    const availableChars = maxChars - reservedChars

    if (availableChars <= 0) {
      return `${ellipsis}${groupNumber}`
    }

    const truncatedSchool = schoolName.slice(0, availableChars)
    return `${truncatedSchool}${ellipsis}${groupNumber}`
  }

  // If no group number, just return the school name (truncated if necessary)
  return schoolName.length <= maxChars ? schoolName : schoolName.slice(0, maxChars)
}

/**
 * Alternative version of truncate group that can handle different formats
 */
export const alternativeTruncateGroup = groupName => {
  if (!groupName) return ""

  // Example: "SP25-NYC(01M361 Children's Workshop)-03"
  const matches = groupName.match(/^([^-]+)-([^(]+)\(([^)]+)\)-(\d+)$/)
  if (!matches) return groupName

  const schoolName = matches[3] // "01M361 Children's Workshop"
  const groupNumber = matches[4] // "03"

  // Take as many characters from school name as possible
  let truncated = schoolName.slice(0, MAX_CHARS.DESKTOP - 3) // -3 for "..." and group number
  if (truncated.length < schoolName.length) {
    truncated += "..."
  }

  return `${truncated}${groupNumber}`
}
